// GENERATED FILE. DO NOT EDIT.
//
// One method per operation on the public 'tiles' surface, generated from
// the mountayaapp/schemas contract (openapi/tiles.yaml).
//
// Method names are the spec's operationIds verbatim.
// Edit the spec in mountayaapp/schemas, not this file.

import type { RequestOptions, MountayaTransport } from "../core/rules/transport.js";
import type { operations } from "./schema.gen.js";

/**
 * SuccessBody extracts the JSON body of an operation's 2xx response: its 200, else its 201,
 * else its 202.
 *
 * The platform wraps every success in an envelope whose `data` carries the payload, so
 * this resolves to the envelope rather than to the bare resource: callers read
 * `result.data`, which is what the wire actually sends, `null` on a write that removes
 * something and has nothing left to answer. A 202 is read like the other two: an operation
 * that starts work rather than finishing it names what it started at `data`, and that is
 * typed only if this reaches it.
 */
type SuccessBody<O> = O extends {
  responses: { 200: { content: { "application/json": infer B } } };
}
  ? B
  : O extends { responses: { 201: { content: { "application/json": infer C } } } }
    ? C
    : O extends { responses: { 202: { content: { "application/json": infer D } } } }
      ? D
      : unknown;

/**
 * Retrieve the tile specification for a collection.
 * GET /v1/collections/{collection_id}/specification
 * Requires a publishable key with the `tiles` scope.
 */
export const getCollectionSpecification = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  readonly query?: NonNullable<operations["getCollectionSpecification"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getCollectionSpecification"]>> =>
  transport.request({
    acceptsShareToken: true,
    idempotent: true,
    method: "GET",
    operationId: "getCollectionSpecification",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}/specification`,
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "tiles",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve the tile specification for the organization's itineraries.
 * GET /v1/itineraries/specification
 * Requires a publishable key with the `tiles` scope.
 */
export const getItinerariesSpecification = (transport: MountayaTransport, options?: RequestOptions): Promise<SuccessBody<operations["getItinerariesSpecification"]>> =>
  transport.request({
    acceptsShareToken: true,
    idempotent: true,
    method: "GET",
    operationId: "getItinerariesSpecification",
    path: "/v1/itineraries/specification",
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "tiles",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve the tile specification for a single itinerary.
 * GET /v1/itineraries/{itinerary_id}/specification
 * Requires a publishable key with the `tiles` scope.
 */
export const getItinerarySpecification = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
}, options?: RequestOptions): Promise<SuccessBody<operations["getItinerarySpecification"]>> =>
  transport.request({
    acceptsShareToken: true,
    idempotent: true,
    method: "GET",
    operationId: "getItinerarySpecification",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/specification`,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "tiles",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve the tile specification for a map overlay.
 * GET /v1/overlays/{overlay}/specification
 * Requires a publishable key with the `tiles` scope.
 */
export const getOverlaySpecification = (transport: MountayaTransport, args: {
  readonly "overlay": string;
  readonly query?: NonNullable<operations["getOverlaySpecification"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOverlaySpecification"]>> =>
  transport.request({
    acceptsShareToken: true,
    idempotent: true,
    method: "GET",
    operationId: "getOverlaySpecification",
    path: `/v1/overlays/${encodeURIComponent(String(args.overlay))}/specification`,
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "tiles",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List the terrains, overlays and layers this API draws.
 * GET /v1/tile-layers
 * Requires a publishable key with the `tiles` scope.
 */
export const getTileLayers = (transport: MountayaTransport, args: {
  readonly query?: NonNullable<operations["getTileLayers"]["parameters"]["query"]> | undefined;
} = {}, options?: RequestOptions): Promise<SuccessBody<operations["getTileLayers"]>> =>
  transport.request({
    acceptsShareToken: true,
    idempotent: true,
    method: "GET",
    operationId: "getTileLayers",
    path: "/v1/tile-layers",
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "tiles",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve the tile specification for the organization's zones.
 * GET /v1/zones/specification
 * Requires a publishable key with the `tiles` scope.
 */
export const getZonesSpecification = (transport: MountayaTransport, options?: RequestOptions): Promise<SuccessBody<operations["getZonesSpecification"]>> =>
  transport.request({
    acceptsShareToken: true,
    idempotent: true,
    method: "GET",
    operationId: "getZonesSpecification",
    path: "/v1/zones/specification",
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "tiles",
    timeoutMs: options?.timeoutMs,
  });

/**
 * bind returns every 'tiles' operation with the transport applied.
 */
export const bind = (transport: MountayaTransport) => ({
  /**
   * Retrieve the tile specification for a collection.
   * GET /v1/collections/{collection_id}/specification
   * Requires a publishable key with the `tiles` scope.
   * @see {@link getCollectionSpecification}
   */
  getCollectionSpecification: (args: Parameters<typeof getCollectionSpecification>[1], options?: RequestOptions) => getCollectionSpecification(transport, args, options),
  /**
   * Retrieve the tile specification for the organization's itineraries.
   * GET /v1/itineraries/specification
   * Requires a publishable key with the `tiles` scope.
   * @see {@link getItinerariesSpecification}
   */
  getItinerariesSpecification: (options?: RequestOptions) => getItinerariesSpecification(transport, options),
  /**
   * Retrieve the tile specification for a single itinerary.
   * GET /v1/itineraries/{itinerary_id}/specification
   * Requires a publishable key with the `tiles` scope.
   * @see {@link getItinerarySpecification}
   */
  getItinerarySpecification: (args: Parameters<typeof getItinerarySpecification>[1], options?: RequestOptions) => getItinerarySpecification(transport, args, options),
  /**
   * Retrieve the tile specification for a map overlay.
   * GET /v1/overlays/{overlay}/specification
   * Requires a publishable key with the `tiles` scope.
   * @see {@link getOverlaySpecification}
   */
  getOverlaySpecification: (args: Parameters<typeof getOverlaySpecification>[1], options?: RequestOptions) => getOverlaySpecification(transport, args, options),
  /**
   * List the terrains, overlays and layers this API draws.
   * GET /v1/tile-layers
   * Requires a publishable key with the `tiles` scope.
   * @see {@link getTileLayers}
   */
  getTileLayers: (args?: Parameters<typeof getTileLayers>[1], options?: RequestOptions) => getTileLayers(transport, args, options),
  /**
   * Retrieve the tile specification for the organization's zones.
   * GET /v1/zones/specification
   * Requires a publishable key with the `tiles` scope.
   * @see {@link getZonesSpecification}
   */
  getZonesSpecification: (options?: RequestOptions) => getZonesSpecification(transport, options),
});
